import { useCallback, useEffect, useMemo, useState } from "react";
import { taskService } from "@miplatform/api";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import {
  AutoAssignWorkOrderTemplateStatus,
  CalendarEventType,
  IAutoAssignWorkOrderTemplateStatus,
} from "@miplatform/types";
import { addToDate, formatDate, isDateAfter } from "@miplatform/utils";

type UseAutoAssignStatusIndicatorArgs = {
  calendarSetId?: string;
  eventType?: CalendarEventType;
  autoAssignStatusUpdates?: IAutoAssignWorkOrderTemplateStatus[];
};

export function useAutoAssignStatusIndicator({
  calendarSetId,
  eventType,
  autoAssignStatusUpdates,
}: UseAutoAssignStatusIndicatorArgs) {
  const [autoAssignStatuses, setAutoAssignStatuses] = useState<
    IAutoAssignWorkOrderTemplateStatus[]
  >([]);
  const [autoAssignCountdownTime, setAutoAssignCountdownTime] = useState(
    Date.now()
  );

  const shouldLoadAutoAssignStatus =
    eventType === CalendarEventType.WorkOrder && !!calendarSetId;

  const loadAutoAssignStatuses = useCallback(async () => {
    if (!shouldLoadAutoAssignStatus || !calendarSetId) {
      setAutoAssignStatuses([]);
      return;
    }

    const response = await taskService.workOrders.getAutoAssignStatuses({
      calendarSetId,
    });

    setAutoAssignStatuses(response?.data ?? []);
  }, [calendarSetId, shouldLoadAutoAssignStatus]);

  const latestAutoAssignStatus = useMemo(
    () => getLatestAutoAssignStatus(autoAssignStatuses),
    [autoAssignStatuses]
  );
  const isAutoAssignQueued =
    latestAutoAssignStatus?.status === AutoAssignWorkOrderTemplateStatus.Queued;
  const hasActiveAutoAssignStatus = isActiveStatus(
    latestAutoAssignStatus?.status
  );
  const autoAssignIndicatorLabel = useMemo(
    () =>
      getAutoAssignIndicatorLabel(
        latestAutoAssignStatus,
        autoAssignCountdownTime
      ),
    [autoAssignCountdownTime, latestAutoAssignStatus]
  );

  useEffect(() => {
    loadAutoAssignStatuses();
  }, [loadAutoAssignStatuses]);

  useEffect(() => {
    if (!shouldLoadAutoAssignStatus || !calendarSetId) {
      return;
    }

    const updates = (autoAssignStatusUpdates ?? []).filter(
      (status) => status?.calendarSetId === calendarSetId
    );
    if (!updates.length) {
      return;
    }

    setAutoAssignStatuses((current) => mergeStatusUpdates(current, updates));
  }, [autoAssignStatusUpdates, calendarSetId, shouldLoadAutoAssignStatus]);

  useEffect(() => {
    if (!hasActiveAutoAssignStatus) {
      return undefined;
    }

    const interval = window.setInterval(loadAutoAssignStatuses, 2000);
    return () => clearInterval(interval);
  }, [hasActiveAutoAssignStatus, loadAutoAssignStatuses]);

  useEffect(() => {
    if (!isAutoAssignQueued) {
      return undefined;
    }

    setAutoAssignCountdownTime(Date.now());
    const interval = setInterval(
      () => setAutoAssignCountdownTime(Date.now()),
      1000
    );
    return () => clearInterval(interval);
  }, [isAutoAssignQueued]);

  return {
    autoAssignIndicatorLabel,
    latestAutoAssignStatus,
    shouldLoadAutoAssignStatus,
    isAutoAssignActive: hasActiveAutoAssignStatus,
    loadAutoAssignStatuses,
  };
}

function getLatestAutoAssignStatus(
  statuses: IAutoAssignWorkOrderTemplateStatus[]
) {
  return statuses.reduce<IAutoAssignWorkOrderTemplateStatus | undefined>(
    (latest, status) =>
      !latest || isDateAfter(status.startedOn, latest.startedOn)
        ? status
        : latest,
    undefined
  );
}

function getAutoAssignIndicatorLabel(
  status?: IAutoAssignWorkOrderTemplateStatus,
  currentTime = Date.now()
) {
  if (!status) {
    return "No auto assign runs found";
  }

  if (status.status === AutoAssignWorkOrderTemplateStatus.Queued) {
    const startsAt = addToDate(status.startedOn, 10, "minutes");
    if (startsAt.seconds() > 0 || startsAt.milliseconds() > 0) {
      startsAt.add(1, "minute").startOf("minute");
    }

    return `Auto assign starts in ${formatCountdown(
      startsAt.toDate(),
      currentTime
    )}`;
  }

  if (status.status === AutoAssignWorkOrderTemplateStatus.InProgress) {
    return "Auto assign running";
  }

  if (status.status === AutoAssignWorkOrderTemplateStatus.Failed) {
    return `Auto assign failed at ${formatDate(
      status.completedOn ?? status.startedOn,
      DATE_TIME_FORMATS.FORMAT_hh_mm_ss_A
    )}`;
  }

  // by default, assume the auto assign has completed successfully
  return `Auto assign completed at ${formatDate(
    status.completedOn,
    DATE_TIME_FORMATS.FORMAT_hh_mm_ss_A
  )}`;
}

function isActiveStatus(status?: AutoAssignWorkOrderTemplateStatus) {
  return (
    status === AutoAssignWorkOrderTemplateStatus.Queued ||
    status === AutoAssignWorkOrderTemplateStatus.InProgress
  );
}

function formatCountdown(targetTime: string | Date, currentTime: number) {
  const remainingSeconds = Math.max(
    0,
    Math.ceil((new Date(targetTime).getTime() - currentTime) / 1000)
  );
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function mergeStatusUpdates(
  current: IAutoAssignWorkOrderTemplateStatus[],
  updates: IAutoAssignWorkOrderTemplateStatus[]
) {
  const byTemplateId = new Map(
    current.map((status) => [status.workOrderTemplateId, status])
  );

  for (const update of updates) {
    byTemplateId.set(update.workOrderTemplateId, update);
  }

  return [...byTemplateId.values()];
}
